import React from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import { DialogContent, DialogContentText } from "@material-ui/core";
import { IPlannerState } from "../../../store/modules/plannerSlice";
import { checkIfOverlapForTrips } from "./conflictNoticeHelper";

interface TripChangedNoticeProps {
    plannerStore: IPlannerState
}

export enum TripChangedNoticeType {
    overlap = 1,
    localStateUpdate = 2
}

const useStyles = makeStyles({
    dialog: {
        width: 400,
        background: 'white',
        borderRadius: 0
    },
    content: {
        color: '#00152E',
        fontSize: 16,
        fontWeight: 700,
        fontFamily: 'lato',
        lineHeight: 'normal',
        textAlign: 'left'
    },
    icon: {
        'filter': 'invert(36%) sepia(96%) saturate(1083%) hue-rotate(331deg) brightness(101%) contrast(89%)'
    },
    iconContainer: {
        textAlign: 'left',
        margin: '5px 0px 20px 0px',
        fontSize: 18,
        fontWeight: 800,
        color: '#00152E'
    },
    onlyOneButtonStyle: {
        width: '100%'
    }
});


const getDialogText = (messageType: TripChangedNoticeType | null, changedNames: string): string => {
    if(!messageType){
        return '';
    }
    switch (messageType) {
        case TripChangedNoticeType.overlap:
            return 'The update brought overlaps.'
        case TripChangedNoticeType.localStateUpdate:
            return `The transfer(s) for ${changedNames} have been changed by someone else. As a result, your changes for this transfer have had to be discarded.`
    }
}

const TripChangedNotice: React.FC<TripChangedNoticeProps> = ({
    plannerStore
}) => {
    const [noticeType, setNoticeType] = React.useState<TripChangedNoticeType| null>(null);
    const [lastGlobalUpdateAt, setLastGlobalUpdateAt] = React.useState<string | null>(null);
    const [lastLocalStateDiscardedAt, setLastLocalStateDiscardedAt] = React.useState<string | null>(null);
    const classes = useStyles();

    React.useEffect(() => {
        if(lastGlobalUpdateAt === null || noticeType){
            setLastGlobalUpdateAt(plannerStore.lastGlobalUpdateAt);
            setLastLocalStateDiscardedAt(plannerStore.lastLocalStateDiscardedAt);
            return;
        }
        if(lastLocalStateDiscardedAt !== plannerStore.lastLocalStateDiscardedAt){
            setNoticeType(TripChangedNoticeType.localStateUpdate);
        }else if(plannerStore.localState.length > 0 && 
            lastGlobalUpdateAt !== plannerStore.lastGlobalUpdateAt){
            const isOverlaped = checkIfOverlapForTrips(plannerStore.globalState, plannerStore.localState);
            if(isOverlaped){
                setNoticeType(TripChangedNoticeType.overlap);
            }
        }
        setLastGlobalUpdateAt(plannerStore.lastGlobalUpdateAt);
        setLastLocalStateDiscardedAt(plannerStore.lastLocalStateDiscardedAt);
    }, [plannerStore.lastGlobalUpdateAt])
    const dialogText = getDialogText(
        noticeType,
        plannerStore.lastChangedNames
    );
    
    return (
        <Dialog open={noticeType ? true:false}
            onClose={() => setNoticeType(null)}
            PaperProps={{ className: classes.dialog }}>
            <DialogContent>
                <div className={classes.iconContainer} >
                    Transfer Changed
                </div>
                <DialogContentText>
                    <div className={classes.content}>{dialogText}</div>
                    <div className={classes.content} style={{marginTop:20}}>
                        Please review this transfer(s) and adjust as necessary. 
                    </div>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setNoticeType(null)} color="primary"
                    className={classes.onlyOneButtonStyle}>
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TripChangedNotice;
